import { SEVERITY_LEVEL_MAP } from "@/atoms/indicators/SeverityLevelIndicator/constants";
import { NgTooltip } from "@/atoms/tooltips";
import { useId } from "react";

import { CodeSecurityIssueSeverity } from "@oneleet/api_client";
import { cn } from "@oneleet/utils";

import { VariantProps, cva } from "class-variance-authority";

const fillColorVariants = cva("fill-white", {
  variants: {
    level: {
      [CodeSecurityIssueSeverity.CRITICAL]: "fill-ng-plum-900",
      [CodeSecurityIssueSeverity.HIGH]: "fill-ng-red-900",
      [CodeSecurityIssueSeverity.MEDIUM]: "fill-ng-orange-900",
      [CodeSecurityIssueSeverity.LOW]: "fill-ng-yellow-900",
      [CodeSecurityIssueSeverity.INFO]: "fill-accent-800",
    },
    filled: {
      true: "",
    },
  },
});

type SeverityLevelIndicatorProps = {
  className?: string;
  level?: CodeSecurityIssueSeverity;
  hasTooltip?: boolean;
} & VariantProps<typeof fillColorVariants>;

export function SeverityLevelIndicator({
  level,
  hasTooltip = false,
  className,
}: SeverityLevelIndicatorProps) {
  const clipPathId = useId();
  if (!level) {
    return null;
  }

  const wrapInTooltip = (node: React.ReactNode) =>
    hasTooltip ? (
      <NgTooltip delayDuration={100}>
        <NgTooltip.Trigger>{node}</NgTooltip.Trigger>
        <NgTooltip.Content>
          {SEVERITY_LEVEL_MAP[level]} severity
        </NgTooltip.Content>
      </NgTooltip>
    ) : (
      node
    );

  return wrapInTooltip(
    <svg
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={cn("size-4", className)}
    >
      {getSeverityIcon(level, clipPathId)}
    </svg>,
  );
}

const getSeverityIcon = (
  level: CodeSecurityIssueSeverity,
  clipPathId: string,
) => {
  switch (level) {
    case CodeSecurityIssueSeverity.CRITICAL:
      return (
        <>
          <g clipPath={`url(#${clipPathId})`}>
            <rect
              x="1"
              y="1"
              width="14"
              height="14"
              rx="2"
              fill="currentColor"
              className={cn(
                fillColorVariants({
                  level,
                  filled: true,
                }),
              )}
            />
            <path
              d="M11.2311 6.85245H9.81063C9.77749 6.64175 9.71239 6.45472 9.61532 6.29137C9.52063 6.12565 9.39989 5.98597 9.25311 5.87234C9.10869 5.75633 8.94179 5.66874 8.7524 5.60955C8.56537 5.548 8.36414 5.51722 8.1487 5.51722C7.76044 5.51722 7.41835 5.61429 7.12242 5.80842C6.82886 6.00018 6.59922 6.28072 6.4335 6.65004C6.26778 7.01935 6.18492 7.46916 6.18492 7.99947C6.18492 8.54161 6.26778 8.99734 6.4335 9.36665C6.60159 9.73361 6.83241 10.0118 7.12598 10.2012C7.41954 10.3882 7.75808 10.4817 8.1416 10.4817C8.35467 10.4817 8.55353 10.4533 8.73819 10.3965C8.92285 10.3397 9.08857 10.2568 9.23535 10.1479C9.38213 10.0366 9.50524 9.90169 9.60467 9.74308C9.7041 9.58209 9.77276 9.39862 9.81063 9.19265L11.2311 9.19975C11.1861 9.54066 11.0819 9.86381 10.9186 10.1692C10.7552 10.4746 10.5398 10.7457 10.2723 10.9824C10.0071 11.2168 9.69345 11.4015 9.33123 11.5364C8.97138 11.669 8.56774 11.7353 8.12029 11.7353C7.47872 11.7353 6.90462 11.5885 6.39799 11.2949C5.89373 10.999 5.496 10.5729 5.20481 10.0165C4.91362 9.4578 4.76802 8.78545 4.76802 7.99947C4.76802 7.21112 4.9148 6.53877 5.20836 5.98242C5.50192 5.42608 5.90083 5.00112 6.40509 4.70756C6.91172 4.41164 7.48346 4.26367 8.12029 4.26367C8.53223 4.26367 8.91456 4.32167 9.26731 4.43768C9.62006 4.55368 9.93374 4.72295 10.2084 4.94549C10.4854 5.16566 10.7114 5.43673 10.8866 5.7587C11.0642 6.0783 11.179 6.44289 11.2311 6.85245Z"
              fill="white"
            />
          </g>
          <defs>
            <clipPath id={clipPathId}>
              <rect width="16" height="16" fill="white" />
            </clipPath>
          </defs>
        </>
      );
    case CodeSecurityIssueSeverity.HIGH:
      return (
        <>
          <rect
            x="1"
            y="1"
            width="14"
            height="14"
            rx="2"
            fill="currentColor"
            className={cn(
              fillColorVariants({
                level,
                filled: true,
              }),
            )}
          />
          <path
            d="M4.95898 11.636V4.36328H6.36523V7.41016H9.63583V4.36328H11.0421V11.636H9.63583V8.58203H6.36523V11.636H4.95898Z"
            fill="white"
          />
        </>
      );
    case CodeSecurityIssueSeverity.MEDIUM:
      return (
        <>
          <rect
            x="1"
            y="1"
            width="14"
            height="14"
            rx="2"
            fill="currentColor"
            className={cn(
              fillColorVariants({
                level,
                filled: true,
              }),
            )}
          />
          <path
            d="M4.13477 4.36328H5.86062L7.95934 9.48047H8.04457L10.1397 4.36328H11.8656V11.636H10.5126V6.74609H10.4451L8.48846 11.6076H7.51545L5.55522 6.73189H5.4913V11.636H4.13477V4.36328Z"
            fill="white"
          />
        </>
      );
    case CodeSecurityIssueSeverity.LOW:
      return (
        <>
          <rect
            x="1"
            y="1"
            width="14"
            height="14"
            rx="2"
            fill="currentColor"
            className={cn(
              fillColorVariants({
                level,
                filled: true,
              }),
            )}
          />
          <path
            d="M5.71129 11.636V4.36328H7.11754V10.4677H10.2887V11.636H5.71129Z"
            fill="white"
          />
        </>
      );
    case CodeSecurityIssueSeverity.INFO:
      return (
        <>
          <rect
            x="1"
            y="1"
            width="14"
            height="14"
            rx="2"
            fill="currentColor"
            className={cn(
              fillColorVariants({
                level,
                filled: true,
              }),
            )}
          />
          <path
            d="M8.70312 4.36328V11.636H7.29688V4.36328H8.70312Z"
            fill="white"
          />
        </>
      );
    default:
      return null;
  }
};
